import { useState } from 'react';
import { useSession } from '../lib/session';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { timezoneHint } from '../lib/timezones';
import { AssignOwnerDialog } from '../features/assignments/AssignOwnerDialog';
import {
  EndAssignmentDialog,
  type EndableAssignment,
} from '../features/assignments/EndAssignmentDialog';
import type {
  AdminRole,
  AdminUser,
  AuditEvent,
  BusinessCalendar,
  OwnershipRow,
  Page,
  SlaCadenceRow,
} from '../lib/types';
import {
  ASSIGNMENT_LABEL,
  IMPORTANCE_TONE,
  formatDate,
  formatDateTime,
  titleCase,
} from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNotice,
  Input,
  Loading,
  PageHeader,
  SectionNote,
  Select,
  StatTile,
  Checkbox,
  Dialog,
  Field,
  Table,
  Td,
  Th,
  Tr,
  cn,
  useToast,
} from '../components/ui';

/**
 * §43 Users & Roles.
 *
 * Renders the permission matrix as configured, because the §62 acceptance
 * criteria are claims about this table and should be checkable by reading it
 * rather than by reading SQL.
 */
/** V1 roles. Future keys (§43) exist in the vocabulary but are not provisioned. */
const ASSIGNABLE_ROLES = [
  'CSM',
  'CS_LEADERSHIP',
  'PRODUCT_MANAGER',
  'PRODUCT_TEAM',
  'ENGINEER',
  'SUPPORT',
  'EXECUTIVE',
  'ADMIN',
] as const;

export function UsersRolesPage() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const { me } = useSession();

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<{ data: AdminUser[] }>('/admin/users'),
  });

  const roles = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<{ data: AdminRole[] }>('/admin/roles'),
  });

  return (
    <>
      <PageHeader
        title="Users & Roles"
        description="Authorization is Role + Scope + Sensitivity + Action. Adding a Finance or Legal role later is configuration here, not a schema change."
      />

      {creating && <UserDialog onClose={() => setCreating(false)} />}
      {editing && <UserDialog user={editing} onClose={() => setEditing(null)} />}

      {(users.isLoading || roles.isLoading) && <Loading />}
      {users.error && <ErrorNotice error={users.error} onRetry={() => users.refetch()} />}

      <div className="space-y-4">
        {users.data && (
          <Card padded={false}>
            <div className="flex items-start justify-between gap-3 px-4 pt-4">
              <CardHeader title="Users" count={users.data.data.length} />
              {me.capabilities.canManageUsers && (
                <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                  New user
                </Button>
              )}
            </div>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Title</Th>
                  <Th>Roles</Th>
                  <Th>Status</Th>
                  <Th align="right">Accounts with access</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {users.data.data.map((user) => (
                  <Tr key={user.id}>
                    <Td className="font-medium">{user.fullName}</Td>
                    <Td className="font-mono text-xs text-ink-2">{user.email}</Td>
                    <Td className="text-ink-2">{user.title ?? '—'}</Td>
                    <Td>
                      <span className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <Badge key={role} tone="neutral" dot={false}>
                            {role}
                          </Badge>
                        ))}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={user.status === 'active' ? 'good' : 'neutral'}>
                        {titleCase(user.status)}
                      </Badge>
                    </Td>
                    <Td align="right" className="tnum">
                      {user.activeAccountCount}
                    </Td>
                    <Td align="right">
                      {me.capabilities.canManageUsers && (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(user)}>
                          Edit
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        {roles.data && (
          <Card padded={false}>
            <div className="px-4 pt-4">
              <CardHeader
                title="Roles and permissions"
                count={roles.data.data.length}
                description="Expand a role to see every grant it holds."
              />
            </div>
            <ul className="divide-y divide-border border-t border-border">
              {roles.data.data.map((role) => {
                const isOpen = expanded === role.key;
                const hasAudit = role.permissions.some((p) => p.resource === 'audit_event');
                const hasWildcard = role.permissions.some((p) => p.resource === '*');
                const sourceContent = role.permissions.filter(
                  (p) => p.resource === 'source_content',
                );
                return (
                  <li key={role.id}>
                    <button
                      onClick={() => setExpanded(isOpen ? null : role.key)}
                      className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left hover:bg-surface-2"
                    >
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-sm font-medium text-ink">
                          {role.key}
                          <span className="text-xs font-normal text-muted">{role.name}</span>
                        </p>
                        <p className="mt-0.5 max-w-3xl text-xs text-ink-2">{role.description}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {hasWildcard && (
                            <Badge tone="info" dot={false}>
                              All resources
                            </Badge>
                          )}
                          <Badge tone={hasAudit ? 'info' : 'neutral'} dot={false}>
                            {hasAudit ? 'Audit access' : 'No audit access'}
                          </Badge>
                          {sourceContent.length > 0 && (
                            <Badge tone="neutral" dot={false}>
                              Source evidence: {sourceContent.map((p) => p.scope).join(', ')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="tnum text-sm text-ink">{role.permissions.length}</p>
                        <p className="text-xs text-muted">grants</p>
                        <p className="mt-1 text-xs text-muted">{role.userCount} users</p>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-border bg-surface-2/40 px-4 py-3">
                        <Table>
                          <thead>
                            <tr>
                              <Th>Resource</Th>
                              <Th>Action</Th>
                              <Th>Scope</Th>
                              <Th>Sensitivities</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...role.permissions]
                              .sort(
                                (a, b) =>
                                  a.resource.localeCompare(b.resource) ||
                                  a.action.localeCompare(b.action),
                              )
                              .map((permission, index) => (
                                <Tr key={`${permission.resource}-${permission.action}-${index}`}>
                                  <Td className="font-mono text-xs">{permission.resource}</Td>
                                  <Td className="font-mono text-xs">{permission.action}</Td>
                                  <Td className="font-mono text-xs">{permission.scope}</Td>
                                  <Td>
                                    <span className="flex flex-wrap gap-1">
                                      {permission.sensitivities.map((level) => (
                                        <Badge key={level} tone="neutral" dot={false}>
                                          {level}
                                        </Badge>
                                      ))}
                                    </span>
                                  </Td>
                                </Tr>
                              ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        <SectionNote>
          Two of §62's criteria are visible above. CSM holds no <code>audit_event</code> grant at
          all, so full operational access to an account still does not confer audit access. And
          PRODUCT_MANAGER holds <code>source_content</code> only at PRODUCT_GLOBAL scope — the
          server reads that as "sources attached to a product request", which is what stops Product
          browsing an account's email history.
        </SectionNote>
      </div>
    </>
  );
}

/** Admin → Account Ownership. */
export function OwnershipPage() {
  const { me } = useSession();
  const query = useQuery({
    queryKey: ['admin-ownership'],
    queryFn: () => api.get<{ data: OwnershipRow[] }>('/admin/account-ownership'),
  });

  /** The account being assigned to, and the assignment being ended. */
  const [assigningTo, setAssigningTo] = useState<OwnershipRow | null>(null);
  const [ending, setEnding] = useState<EndableAssignment | null>(null);
  const canManage = me.capabilities.canManageAssignments;

  const rows = query.data?.data ?? [];
  const withoutSecondary = rows.filter(
    (row) => !row.assignments.some((a) => a.assignmentType === 'secondary_csm'),
  ).length;
  const withCoverage = rows.filter((row) =>
    row.assignments.some((a) => a.assignmentType === 'temporary_coverage'),
  ).length;

  return (
    <>
      <PageHeader
        title="Account Ownership"
        description="Who holds what, right now. Only assignments in force today are shown — temporary coverage expires by date."
      />

      {assigningTo && (
        <AssignOwnerDialog
          accountId={assigningTo.accountId}
          onClose={() => setAssigningTo(null)}
        />
      )}
      {ending && <EndAssignmentDialog assignment={ending} onClose={() => setEnding(null)} />}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Accounts" value={rows.length} />
        <StatTile
          label="No secondary CSM"
          value={withoutSecondary}
          tone="warning"
          context="Single point of failure"
        />
        <StatTile label="Under temporary coverage" value={withCoverage} tone="info" />
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {query.data && rows.length === 0 && (
        <EmptyState
          title="No accounts to show ownership for"
          description="Ownership is a view of the assignments on your accounts, so it stays empty until there are accounts. Create one, then use Assign owner on the account to add a secondary CSM, an executive sponsor, or temporary coverage."
          action={
            <Link
              to="/accounts"
              className="mt-1 text-sm font-medium text-accent hover:underline"
            >
              Go to Accounts
            </Link>
          }
        />
      )}

      {rows.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th>Enterprise</Th>
                <Th>Importance</Th>
                <Th>Active assignments</Th>
                {canManage && <Th align="right">Actions</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Tr key={row.accountId}>
                  <Td>
                    <Link
                      to={`/accounts/${row.accountId}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {row.accountName}
                    </Link>
                  </Td>
                  <Td className="text-ink-2">{row.enterpriseName}</Td>
                  <Td>
                    <Badge
                      tone={IMPORTANCE_TONE[row.importance]}
                      dot={row.importance === 'strategic'}
                    >
                      {titleCase(row.importance)}
                    </Badge>
                  </Td>
                  <Td>
                    {row.assignments.length === 0 ? (
                      <span className="text-xs text-muted">No active assignments</span>
                    ) : (
                      <ul className="space-y-1">
                        {row.assignments.map((assignment) => (
                          <li
                            key={assignment.assignmentId}
                            className="flex items-center gap-2 text-xs"
                          >
                            <span className="w-36 shrink-0 text-muted">
                              {ASSIGNMENT_LABEL[assignment.assignmentType] ??
                                titleCase(assignment.assignmentType)}
                            </span>
                            <span className="text-ink">{assignment.fullName}</span>
                            {!assignment.operationalAccess && (
                              <Badge tone="neutral" dot={false}>
                                No access
                              </Badge>
                            )}
                            {assignment.endsAt && (
                              <span className="text-muted">
                                until {formatDate(assignment.endsAt)}
                              </span>
                            )}
                            {/*
                              The primary CSM is absent by design: the API refuses to end
                              it, because an account with no primary CSM is not a state the
                              model allows. It changes by reassignment on the account.
                            */}
                            {canManage && assignment.assignmentType !== 'primary_csm' && (
                              <button
                                type="button"
                                className="text-muted underline underline-offset-2 hover:text-ink"
                                onClick={() =>
                                  setEnding({
                                    assignmentId: assignment.assignmentId,
                                    accountId: row.accountId,
                                    accountName: row.accountName,
                                    fullName: assignment.fullName,
                                    assignmentType: assignment.assignmentType,
                                  })
                                }
                              >
                                End
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Td>
                  {canManage && (
                    <Td align="right">
                      <Button size="sm" onClick={() => setAssigningTo(row)}>
                        Assign
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}

/** Admin → SLA & Cadence. */
export function SlaCadencePage() {
  const query = useQuery({
    queryKey: ['admin-sla-cadence'],
    queryFn: () =>
      api.get<{ data: SlaCadenceRow[]; calendars: BusinessCalendar[] }>('/admin/sla-cadence'),
  });

  const rows = query.data?.data ?? [];
  const missingPlan = rows.filter((row) => !row.hasSuccessPlan).length;
  const missingSla = rows.filter((row) => row.responseSlaBusinessHours === null).length;

  return (
    <>
      <PageHeader
        title="SLA & Cadence"
        description="The standard each account is measured against. An account with no success plan has no standard, so it cannot appear in cadence or SLA reporting."
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="Accounts" value={rows.length} />
        <StatTile label="No success plan" value={missingPlan} tone="warning" />
        <StatTile label="No response SLA" value={missingSla} tone="warning" />
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {query.data && rows.length === 0 && (
        <EmptyState
          title="No accounts to measure yet"
          description="Every figure here is derived from an account's success plan, so this page fills in once accounts exist and have plans against them."
          action={
            <Link to="/accounts" className="mt-1 text-sm font-medium text-accent hover:underline">
              Go to Accounts
            </Link>
          }
        />
      )}

      {rows.length > 0 && (
        <div className="space-y-4">
          <Card padded={false}>
            <Table>
              <thead>
                <tr>
                  <Th>Account</Th>
                  <Th>Enterprise</Th>
                  <Th>Importance</Th>
                  <Th>Timezone</Th>
                  <Th>Business calendar</Th>
                  <Th align="right">Response SLA</Th>
                  <Th>Meeting cadence</Th>
                  <Th>Business review</Th>
                  <Th align="right">Outreach</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Tr key={row.accountId}>
                    <Td>
                      <Link
                        to={`/accounts/${row.accountId}?tab=success-plan`}
                        className="font-medium text-accent hover:underline"
                      >
                        {row.accountName}
                      </Link>
                      {!row.hasSuccessPlan && (
                        <Badge tone="warning" className="ml-2">
                          No plan
                        </Badge>
                      )}
                    </Td>
                    <Td className="text-ink-2">{row.enterpriseName}</Td>
                    <Td>
                      <Badge
                        tone={IMPORTANCE_TONE[row.importance]}
                        dot={row.importance === 'strategic'}
                      >
                        {titleCase(row.importance)}
                      </Badge>
                    </Td>
                    <Td className="font-mono text-xs text-ink-2">{row.timezone}</Td>
                    <Td className="text-xs text-ink-2">{row.calendarName ?? 'Default'}</Td>
                    <Td align="right" className="tnum">
                      {row.responseSlaBusinessHours ? `${row.responseSlaBusinessHours}h` : '—'}
                    </Td>
                    <Td className="text-ink-2">
                      {titleCase(row.meetingCadenceType ?? 'none')}
                      {row.meetingCadenceDays && ` (${row.meetingCadenceDays}d)`}
                    </Td>
                    <Td className="text-ink-2">
                      {titleCase(row.businessReviewType ?? 'none')}
                      {row.businessReviewCadenceDays && ` (${row.businessReviewCadenceDays}d)`}
                    </Td>
                    <Td align="right" className="tnum text-ink-2">
                      {row.proactiveOutreachCadenceDays
                        ? `${row.proactiveOutreachCadenceDays}d`
                        : '—'}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card padded={false}>
            <div className="px-4 pt-4">
              <CardHeader
                title="Business calendars"
                count={query.data?.calendars.length}
                description="Every overdue, SLA and escalation calculation runs on the account's own calendar. A Friday deadline is one business day overdue on Monday, not three."
              />
            </div>
            <Table>
              <thead>
                <tr>
                  <Th>Calendar</Th>
                  <Th>Timezone</Th>
                  <Th>Working days</Th>
                  <Th>Hours</Th>
                  <Th align="right">Holidays</Th>
                  <Th align="right">Default</Th>
                </tr>
              </thead>
              <tbody>
                {(query.data?.calendars ?? []).map((calendar) => (
                  <Tr key={calendar.id}>
                    <Td className="font-medium">{calendar.name}</Td>
                    <Td className="font-mono text-xs text-ink-2">{calendar.timezone}</Td>
                    <Td className="text-xs text-ink-2">
                      {calendar.workingDays.map((day) => DAY_NAMES[day]).join(' ')}
                    </Td>
                    <Td className="tnum text-xs text-ink-2">
                      {minutesToTime(calendar.workStartMinutes)}–
                      {minutesToTime(calendar.workEndMinutes)}
                    </Td>
                    <Td align="right" className="tnum">
                      {calendar.holidayCount}
                    </Td>
                    <Td align="right">
                      {calendar.isDefault && <Badge tone="info" dot={false}>Default</Badge>}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </div>
      )}
    </>
  );
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * §45 Audit viewer.
 *
 * Separately permissioned per §62 — reaching this page at all requires an
 * `audit_event: read` grant, and the API refuses without one regardless of
 * account access.
 */
export function AuditPage() {
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin-audit', { action, actorType, resourceType }],
    queryFn: () =>
      api.get<Page<AuditEvent>>('/admin/audit', {
        action: action || undefined,
        actorType: actorType || undefined,
        resourceType: resourceType || undefined,
        limit: 100,
      }),
  });

  const events = query.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Audit"
        description="Append-only. Every change, who or what made it, and why. The database refuses updates and deletes on this table."
        meta={
          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={action}
              onChange={(event) => setAction(event.target.value)}
              placeholder="Action prefix, e.g. commitment."
              className="w-64"
            />
            <Select
              value={actorType}
              onChange={(event) => setActorType(event.target.value)}
              className="w-40"
            >
              <option value="">Any actor</option>
              <option value="user">Person</option>
              <option value="system">System</option>
              <option value="ai">AI</option>
              <option value="integration">Integration</option>
            </Select>
            <Select
              value={resourceType}
              onChange={(event) => setResourceType(event.target.value)}
              className="w-48"
            >
              <option value="">Any resource</option>
              <option value="account">Account</option>
              <option value="work_item">Work item</option>
              <option value="work_item_dependency">Dependency</option>
              <option value="account_assignment">Assignment</option>
              <option value="success_plan">Success plan</option>
              <option value="project">Project</option>
              <option value="deliverable">Deliverable</option>
              <option value="contact">Contact</option>
              <option value="activity">Activity</option>
              <option value="enterprise">Enterprise</option>
            </Select>
          </div>
        }
      />

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}
      {query.data && events.length === 0 && <EmptyState title="No audit events match" />}

      {events.length > 0 && (
        <>
          <p className="mb-3 text-xs text-muted">
            Showing {events.length} of {query.data?.total} events.
          </p>
          <Card padded={false}>
            <ul className="divide-y divide-border">
              {events.map((event) => {
                const isOpen = expanded === event.id;
                const hasDetail = Boolean(event.beforeJson || event.afterJson);
                return (
                  <li key={event.id}>
                    <button
                      onClick={() => setExpanded(isOpen ? null : event.id)}
                      disabled={!hasDetail}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-2.5 text-left',
                        hasDetail && 'hover:bg-surface-2',
                      )}
                    >
                      <span className="w-36 shrink-0 text-xs text-muted">
                        {formatDateTime(event.createdAt)}
                      </span>
                      <Badge
                        tone={
                          event.actorType === 'user'
                            ? 'neutral'
                            : event.actorType === 'system'
                              ? 'info'
                              : 'warning'
                        }
                        dot={false}
                      >
                        {event.actorType === 'user'
                          ? (event.actorName ?? 'Person')
                          : (event.actorLabel ?? titleCase(event.actorType))}
                      </Badge>
                      <span className="min-w-0 flex-1">
                        <span className="block font-mono text-xs text-ink">{event.action}</span>
                        <span className="mt-0.5 block text-xs text-ink-2">
                          {event.resourceType}
                          {event.accountName && (
                            <>
                              {' · '}
                              <Link
                                to={`/accounts/${event.accountId}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-accent hover:underline"
                              >
                                {event.accountName}
                              </Link>
                            </>
                          )}
                        </span>
                        {event.reason && (
                          <span className="mt-0.5 block text-xs text-ink-2 italic">
                            “{event.reason}”
                          </span>
                        )}
                      </span>
                      {hasDetail && (
                        <span className="shrink-0 text-xs text-muted">{isOpen ? '−' : '+'}</span>
                      )}
                    </button>
                    {isOpen && (
                      <div className="grid grid-cols-1 gap-3 border-t border-border bg-surface-2/40 px-4 py-3 sm:grid-cols-2">
                        <div>
                          <p className="mb-1 text-xs font-medium text-muted">Before</p>
                          <pre className="overflow-x-auto rounded bg-surface p-2 font-mono text-[11px] text-ink-2">
                            {JSON.stringify(event.beforeJson ?? {}, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-medium text-muted">After</p>
                          <pre className="overflow-x-auto rounded bg-surface p-2 font-mono text-[11px] text-ink-2">
                            {JSON.stringify(event.afterJson ?? {}, null, 2)}
                          </pre>
                        </div>
                        {event.requestId && (
                          <p className="text-[11px] text-muted sm:col-span-2">
                            Request {event.requestId}
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        </>
      )}

      <div className="mt-3">
        <SectionNote>
          Only the fields that actually changed are stored, so the trail stays readable and
          sensitive values are not copied wholesale into a table with its own permission boundary.
        </SectionNote>
      </div>
    </>
  );
}

export { Button };

/**
 * Create or edit a user.
 *
 * There is no password field, and that is deliberate rather than missing: under
 * `AUTH_MODE=dev` a person is identified by header, and under OIDC they bind to
 * their subject claim on first sign-in. What an administrator creates here is
 * the record and the roles the identity provider will later attach to.
 */
function UserDialog({ user, onClose }: { user?: AdminUser; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const editing = Boolean(user);

  const [email, setEmail] = useState(user?.email ?? '');
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [title, setTitle] = useState(user?.title ?? '');
  const [timezone, setTimezone] = useState(user?.timezone ?? 'Asia/Kolkata');
  const [status, setStatus] = useState(user?.status ?? 'active');
  const [roles, setRoles] = useState<string[]>(user?.roles ?? []);

  const done = (message: string) => {
    toast(message);
    void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    void queryClient.invalidateQueries({ queryKey: ['dev-users'] });
    onClose();
  };

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/users', {
        email,
        fullName,
        title: title || null,
        timezone,
        roles,
      }),
    onSuccess: () => done('User created'),
  });

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/admin/users/${user!.id}`, {
        fullName,
        title: title || null,
        timezone,
        status,
      });
      // Roles are replaced as a set, so this is idempotent and the audit entry
      // records the whole before/after rather than a stream of single grants.
      await api.put(`/admin/users/${user!.id}/roles`, { roles });
    },
    onSuccess: () => done('User updated'),
  });

  const pending = create.isPending || save.isPending;
  const error = create.error ?? save.error;
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? `Edit ${user!.fullName}` : 'New user'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={pending || !fullName.trim() || (!editing && !emailValid)}
            onClick={() => (editing ? save.mutate() : create.mutate())}
          >
            {pending ? 'Saving…' : editing ? 'Save' : 'Create user'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorNotice error={error} />}

        <Field label="Email" required hint={editing ? 'Email cannot be changed.' : undefined}>
          <Input
            type="email"
            value={email}
            disabled={editing}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="person@smartwinnr.com"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Full name" required>
            <Input value={fullName} onChange={(event) => setFullName(event.target.value)} />
          </Field>
          <Field label="Title">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Timezone"
            hint={timezoneHint(timezone, 'Drives business-hours SLA and cadence for this person.')}
          >
            <TimezoneSelect value={timezone} onChange={setTimezone} />
          </Field>
          {editing && (
            <Field label="Status">
              <Select value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </Field>
          )}
        </div>

        <Field label="Roles" hint="A person with no role can sign in and see nothing.">
          <div className="grid grid-cols-2 gap-1.5">
            {ASSIGNABLE_ROLES.map((role) => (
              <Checkbox
                key={role}
                checked={roles.includes(role)}
                label={role}
                onChange={(event) =>
                  setRoles((current) =>
                    event.target.checked
                      ? [...current, role]
                      : current.filter((r) => r !== role),
                  )
                }
              />
            ))}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}
